import { CartLinesUpdateEvent } from '@shopify/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* almacenamiento no disponible */
    }
  },
};

/* ---------- Añadir al carrito: usa el evento estándar para que Horizon abra su carrito lateral ---------- */

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function addToCart(lines, source) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: res.ok ? data.sections : undefined,
        source: 'patamora',
        didError: !res.ok,
      },
    });

    if (!res.ok) throw new Error(data.description || data.message || 'No se ha podido añadir al carrito.');
    return cart;
  } catch (error) {
    deferred.resolve({ detail: { didError: true, source: 'patamora' } });
    throw error;
  }
}

function selectedVariant(button) {
  const scope = button.closest('[data-pm-buy]') || button.parentElement;
  const checked = scope.querySelector('input[data-pm-variant]:checked');
  if (checked) return checked.value;
  return button.dataset.variant;
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-pm-add]');
  if (!button) return;
  event.preventDefault();

  const variantId = selectedVariant(button);
  const scope = button.closest('[data-pm-buy]') || button.parentElement;
  const errorEl = scope.querySelector('[data-pm-error]');
  if (errorEl) errorEl.textContent = '';
  if (!variantId) return;

  button.setAttribute('aria-busy', 'true');
  try {
    await addToCart([{ id: Number(variantId), quantity: 1 }], button);
    button.classList.add('is-added');
    // Un toque corto en el móvil, a la vez que el "Añadido" (Android; en iPhone no hace nada).
    try {
      navigator.vibrate?.(12);
    } catch (e) {
      /* sin vibración */
    }
    setTimeout(() => button.classList.remove('is-added'), 2200);
  } catch (error) {
    if (errorEl) errorEl.textContent = error.message;
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/* ---------- Escena de scroll del hero ---------- */

function initScene(section) {
  const hairs = [...section.querySelectorAll('[data-hair]')];
  const counter = section.querySelector('[data-hair-count]');
  const stage = section.querySelector('.pm-hero__stage');
  const total = hairs.length;
  let ticking = false;

  const update = () => {
    ticking = false;
    const rect = section.getBoundingClientRect();
    const stickTop = parseFloat(getComputedStyle(stage).top) || 0;
    const travel = Math.max(1, section.offsetHeight - stage.offsetHeight);
    const scrollP = Math.min(1, Math.max(0, (stickTop - rect.top) / travel));
    // El rodillo también se arrastra con el dedo (patamora-v7.js): manda lo que más haya limpiado.
    // Puede pasar un poco de 1 cuando se estira contra el borde; los pelos solo cuentan hasta 1.
    const shown = Math.max(scrollP, section.pmDragP || 0);
    const p = Math.min(1, shown);
    section.style.setProperty('--p', shown.toFixed(4));

    const sweep = 0.075 + p * 0.86;
    let left = 0;
    for (const hair of hairs) {
      const gone = Number(hair.dataset.x) < sweep;
      hair.classList.toggle('is-gone', gone);
      if (!gone) left += 1;
    }
    if (counter) counter.textContent = String(left);
    section.classList.toggle('is-clean', left === 0);
  };

  if (reduceMotion) {
    section.style.setProperty('--p', '1');
    hairs.forEach((h) => h.classList.add('is-gone'));
    if (counter) counter.textContent = '0';
    section.classList.add('is-clean', 'is-static');
    return;
  }

  section.pmUpdate = update;

  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };

  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        document.addEventListener('scroll', onScroll, { passive: true, capture: true });
        window.addEventListener('resize', onScroll);
        update();
      } else {
        document.removeEventListener('scroll', onScroll, { capture: true });
        window.removeEventListener('resize', onScroll);
      }
    }
  });
  io.observe(section);
  if (counter) counter.textContent = String(total);
  update();
}

document.querySelectorAll('[data-pm-scene]').forEach(initScene);

/* ---------- Tarjetas con inclinación 3D ---------- */

if (!reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  document.querySelectorAll('[data-pm-tilt]').forEach((card) => {
    card.addEventListener('pointermove', (e) => {
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      card.style.setProperty('--rx', `${(-y * 8).toFixed(2)}deg`);
      card.style.setProperty('--ry', `${(x * 10).toFixed(2)}deg`);
      card.style.setProperty('--gx', `${((x + 0.5) * 100).toFixed(0)}%`);
      card.style.setProperty('--gy', `${((y + 0.5) * 100).toFixed(0)}%`);
    });
    card.addEventListener('pointerleave', () => {
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
    });
  });
}

/* ---------- Bienvenida: 10 % en la primera compra ---------- */

function initWelcome() {
  const dialog = document.querySelector('[data-pm-welcome]');
  const tab = document.querySelector('[data-pm-welcome-tab]');
  if (!dialog || !tab) return;

  const code = dialog.dataset.code;
  const applied = storage.get('pm-welcome-applied') === '1';
  const seen = storage.get('pm-welcome-seen') === '1';

  const open = () => {
    if (dialog.open) return;
    storage.set('pm-welcome-seen', '1');
    tab.hidden = true;
    dialog.showModal();
  };

  dialog.addEventListener('close', () => {
    if (storage.get('pm-welcome-applied') !== '1') tab.hidden = false;
  });

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });

  dialog.querySelector('[data-pm-copy]')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    try {
      await navigator.clipboard.writeText(code);
      btn.textContent = 'Código copiado';
    } catch (err) {
      btn.textContent = code;
    }
  });

  dialog.querySelector('[data-pm-apply]')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    btn.setAttribute('aria-busy', 'true');
    try {
      await fetch(`${root}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
      storage.set('pm-welcome-applied', '1');
      btn.textContent = 'Descuento activado';
      setTimeout(() => dialog.close(), 900);
    } catch (err) {
      btn.textContent = 'Usa el código al pagar';
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  tab.addEventListener('click', open);

  // Nunca dos capas a la vez: aviso de cookies (y sus preferencias), chat o carrito abiertos.
  // Del carrito se mira el cajón y no su <dialog>, que sigue con [open] mientras hace la animación de cerrarse.
  const shown = (sel) => {
    const el = document.querySelector(sel);
    return !!el && el.getClientRects().length > 0;
  };
  const busy = () => shown('#shopify-pc__banner') || shown('#shopify-pc__prefs') || !!document.querySelector('.pm-chat.is-open, #cart-drawer[open]');

  // La pestaña se aparta mientras haya otra capa abierta.
  setInterval(() => {
    tab.style.display = busy() ? 'none' : '';
  }, 1500);

  if (applied) return;
  if (seen) {
    tab.hidden = false;
    return;
  }

  // La ventana sale a los 10 s si ya se ha pasado el hero (o a los 45 s en cualquier caso),
  // y 3 s después de que se cierre lo que estuviera abierto.
  const started = performance.now();
  const hero = document.querySelector('.pm-hero');
  const deep = () => {
    if (hero) return hero.getBoundingClientRect().bottom < window.innerHeight * 0.4;
    const scroller = document.querySelector('.page-wrapper');
    const el = scroller && scroller.scrollHeight > scroller.clientHeight + 4 ? scroller : document.scrollingElement;
    return el.scrollTop / Math.max(1, el.scrollHeight - el.clientHeight) > 0.35;
  };
  let calmAt = 0;
  const timer = setInterval(() => {
    const now = performance.now();
    if (busy()) {
      calmAt = now + 3000;
      return;
    }
    const waited = now - started;
    if (now < calmAt || !(waited >= 45000 || (waited >= 10000 && deep()))) return;
    clearInterval(timer);
    open();
  }, 2000);
}

initWelcome();
